import { useEffect, useState } from 'react';
import apiClient from '../utils/axios';
import { Filter, Download, RefreshCw } from 'lucide-react';

export default function ContactEnrichment() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    discoveryId: '',
    minIntentScore: '',
    maxIntentScore: '',
    enrichmentTier: '',
    status: ''
  });

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.discoveryId) params.append('discoveryId', filters.discoveryId);
      if (filters.minIntentScore) params.append('minIntentScore', filters.minIntentScore);
      if (filters.maxIntentScore) params.append('maxIntentScore', filters.maxIntentScore);
      if (filters.enrichmentTier) params.append('enrichmentTier', filters.enrichmentTier);
      if (filters.status) params.append('status', filters.status);

      const response = await apiClient.get(`/contact-enrichment?${params.toString()}`);
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.discoveryId) params.append('discoveryId', filters.discoveryId);
      if (filters.minIntentScore) params.append('minIntentScore', filters.minIntentScore);
      if (filters.maxIntentScore) params.append('maxIntentScore', filters.maxIntentScore);
      if (filters.enrichmentTier) params.append('enrichmentTier', filters.enrichmentTier);

      const response = await apiClient.get(`/contact-export?format=csv&${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contacts-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting contacts:', error);
      alert('Error exporting contacts: ' + (error.response?.data?.message || error.message));
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading enriched contacts...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Enriched Contacts</h1>
          <p className="mt-2 text-gray-600">View and manage enriched contacts</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <Download size={20} />
            Export CSV
          </button>
          <button
            onClick={fetchContacts}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 flex items-center gap-2"
          >
            <RefreshCw size={20} />
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Filter size={20} />
          Filters
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Min Intent Score</label>
            <input
              type="number"
              value={filters.minIntentScore}
              onChange={(e) => setFilters({ ...filters, minIntentScore: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              min="0"
              max="100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Intent Score</label>
            <input
              type="number"
              value={filters.maxIntentScore}
              onChange={(e) => setFilters({ ...filters, maxIntentScore: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              min="0"
              max="100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Enrichment Tier</label>
            <select
              value={filters.enrichmentTier}
              onChange={(e) => setFilters({ ...filters, enrichmentTier: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">All</option>
              <option value="basic">Basic</option>
              <option value="standard">Standard</option>
              <option value="deep">Deep</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">All</option>
              <option value="enriched">Enriched</option>
              <option value="verified">Verified</option>
              <option value="queued">Queued</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchContacts}
              className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Intent Score</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {contacts.map((contact) => (
              <tr key={contact.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900">
                    {contact.first_name} {contact.last_name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {contact.email || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {contact.company_name || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {contact.job_title || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    contact.intent_score >= 80 ? 'bg-green-100 text-green-800' :
                    contact.intent_score >= 60 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {contact.intent_score || 'N/A'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                    {contact.enrichment_tier || 'basic'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {contact.status || 'pending'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {contacts.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No enriched contacts found.
          </div>
        )}
      </div>
    </div>
  );
}


